import { useNavigate } from "react-router-dom";
import { Stethoscope, ShieldCheck, FileHeart } from "lucide-react";

const AMBER = "#EF9F27";

export default function Welcome() {
  const navigate = useNavigate();
  const start = () => navigate("/auth");

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] h-screen bg-[#111111] flex flex-col overflow-hidden">
        {/* Ambient amber glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: AMBER }}
        />

        {/* Brand + headline */}
        <header className="relative shrink-0 px-7 pt-14 pb-4">
          <div className="flex items-center gap-2">
            <span
              className="w-10 h-10 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: AMBER }}
            >
              <Stethoscope size={22} className="text-[#111111]" />
            </span>
            <p className="text-xs font-bold tracking-[0.3em] text-[#EF9F27]">POCKETDOCTA</p>
          </div>
          <h1 className="mt-8 font-head text-5xl font-extrabold text-[#F8FAFC] leading-[1.05]">
            Trusted<br />Healthcare.
          </h1>
          <p className="mt-4 text-base text-[#A1A1AA] leading-relaxed">
            Expert second opinions from verified specialists, delivered through your own doctor —
            wherever you live.
          </p>
        </header>

        {/* Feature bullets */}
        <div className="relative flex-1 min-h-0 px-7 pt-6 flex flex-col justify-center gap-4">
          <Feature
            icon={<Stethoscope size={20} className="text-[#EF9F27]" />}
            title="Specialist reviews"
            body="Share your concern and records for a written second opinion."
          />
          <Feature
            icon={<ShieldCheck size={20} className="text-[#EF9F27]" />}
            title="Verified experts"
            body="Every specialist is credential-checked before they can respond."
          />
          <Feature
            icon={<FileHeart size={20} className="text-[#EF9F27]" />}
            title="Your health, organised"
            body="Records, medical ID and travel wellbeing — all in one place."
          />
        </div>

        {/* Footer — CTA + disclaimer + provider access */}
        <footer className="relative shrink-0 px-7 pt-4 pb-6">
          <button
            data-testid="get-started-btn"
            onClick={start}
            className="w-full min-h-[52px] rounded-full text-[#111111] text-base font-extrabold transition-transform active:scale-[0.98]"
            style={{ backgroundColor: AMBER }}
          >
            Get started
          </button>
          <p className="mt-4 text-[11px] text-[#666666] leading-relaxed text-center">
            PocketDocta provides second-opinion information for discussion with your doctor. Not a
            medical device, diagnosis, or emergency service.
          </p>
          <p className="mt-2 text-[11px] text-[#A1A1AA] text-center">
            Are you a medical specialist?{" "}
            <button
              data-testid="welcome-specialist-link"
              onClick={() => navigate("/specialist/login")}
              className="font-semibold underline underline-offset-2 text-[#EF9F27]"
            >
              Apply to join
            </button>
          </p>
          <p className="mt-2 text-[11px] text-[#666666] text-center">
            <button data-testid="welcome-privacy-link" onClick={() => navigate("/privacy")} className="underline underline-offset-2">
              Privacy Policy
            </button>
            {" · "}
            <button data-testid="welcome-terms-link" onClick={() => navigate("/terms")} className="underline underline-offset-2">
              Terms
            </button>
          </p>
        </footer>
      </div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4">
      <span className="mt-0.5 w-9 h-9 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center shrink-0">
        {icon}
      </span>
      <div>
        <h3 className="font-head font-bold text-[#F8FAFC]">{title}</h3>
        <p className="text-sm text-[#A1A1AA] leading-snug">{body}</p>
      </div>
    </div>
  );
}
